import React, { useState, useMemo } from "react";
import type { CalendarEvent } from "../../../types/types";
import { Card, CardContent, CardHeader } from "../../ui/card";
import { SummaryHeader } from "../../ui/SummaryHeader";
import { EventActions } from "./eventActions";
import { EventForm } from "./eventForm";
import { Plus, Calendar, Clock, TrendingUp, TrendingDown } from "lucide-react";
import { Button } from "../../ui/button";
import { useDeleteEvent } from "../hooks/useDeleteEvent";
import { useRevenueBreakdown } from "../../overview/hooks/useRevenueBreakdown";
import { calculateEventMetrics } from "../../../utils/eventCalculations";
import { useEcommRevenueBreakdown } from "../../e-commerce/hooks/useEcommRevenueBreakdown";
import { useWholesaleRevenueBreakdown } from "../../wholesale/hooks/useWholesaleRevenueBreakdown";
import { EventsCardSkeleton } from "../../../components/loaders/EventsCardLoader";

interface EventsCardProps {
  events: CalendarEvent[];
  selectedDate: Date | undefined;
  onEventsChange: (events: CalendarEvent[]) => void;
  currency?: string;
  eventSource?: "data" | "retail" | "wholesale" | "ecom"; // <-- add this
}

export const EventsCard: React.FC<EventsCardProps> = ({
  events,
  selectedDate,
  onEventsChange,
  currency = "$",
  eventSource = "data", // <-- default to "data"
}) => {
  const windowDays = 3; // Number of days for "Before" and "After" periods
  const [showAdd, setShowAdd] = useState(false);
  const [editEvent, setEditEvent] = useState<CalendarEvent | null>(null);
  const [currentDeletingId, setCurrentDeletingId] = useState<string | null>(null);

  const { mutate: deleteEventMutation } = useDeleteEvent();

  // Set up parameters for useRevenueBreakdown
  const params = useMemo(() => {
    const start = events.length
      ? new Date(Math.min(...events.map((evt) => new Date(evt.startAt).getTime())) - windowDays * 24 * 60 * 60 * 1000)
      : undefined;

    const end = events.length
      ? new Date(Math.max(...events.map((evt) => new Date(evt.endAt).getTime())) + windowDays * 24 * 60 * 60 * 1000)
      : undefined;

    let channel = "all_channels";
    if (eventSource === "ecom") channel = "shopify";
    else if (eventSource === "wholesale") channel = "wholesale";
    else if (eventSource === "retail") channel = "shopify";

    return {
      start_date: start ? start.toISOString().slice(0, 10) : "",
      end_date: end ? end.toISOString().slice(0, 10) : "",
      channel, // <-- now dynamic
      kind: "profit",
      period_type: "daily",
    };
  }, [events, eventSource]);

  // Fetch revenue data
  const { data: revenueData, error: revenueError, isLoading: revenueLoading } = useRevenueBreakdown(params);
  const { data: ecommRevenueData } = useEcommRevenueBreakdown(params);
  const { data: wholesaleRevenueData } = useWholesaleRevenueBreakdown(params);

  // Select correct revenue data based on eventSource
  const selectedRevenueData = useMemo(() => {
    switch (eventSource) {
      case "data":
        return revenueData;
      case "ecom":
        return ecommRevenueData;
      case "wholesale":
        return wholesaleRevenueData;
      case "retail":
        return ecommRevenueData; // for now
      default:
        return revenueData;
    }
  }, [eventSource, revenueData, ecommRevenueData, wholesaleRevenueData]);

  // Transform revenue data
  const transformedData = selectedRevenueData
    ? selectedRevenueData.map((item: any) => ({
        date: item.date,
        revenue_value: item.revenue_value,
      }))
    : [];

  // Filter events for the selected date
  const dayEvents = events.filter((evt) => {
    if (!selectedDate) return false;
    const selectedDateStr = selectedDate.toISOString().slice(0, 10);
    const startStr = evt.startAt.slice(0, 10);
    const endStr = evt.endAt.slice(0, 10);
    return selectedDateStr >= startStr && selectedDateStr <= endStr;
  });

  // Add event handler
  function handleAddEvent(newEvent: CalendarEvent) {
    onEventsChange([...events, newEvent]);
    setShowAdd(false);
  }

  // Delete event handler
  function handleDeleteEvent(id: string) {
    setCurrentDeletingId(id);
    deleteEventMutation(id, {
      onSuccess: () => {
        onEventsChange(events.filter((e) => e.id !== id));
        setCurrentDeletingId(null);
      },
      onError: (error) => {
        console.error("Failed to delete event:", error);
        setCurrentDeletingId(null);
      },
    });
  }

  // Get source color helper
  const getSourceColor = (source: string) => {
    switch (source) {
      case "manual":
        return "var(--chart-3)";
      case "meta":
        return "var(--chart-1)";
      default:
        return "var(--chart-5)";
    }
  };

  // Format date range helper
  const formatDateRange = (startAt: string, endAt: string) => {
    const start = new Date(startAt);
    const end = new Date(endAt);
    const startDate = start.toLocaleDateString();
    const endDate = end.toLocaleDateString();
    const startTime = start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const endTime = end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (startDate === endDate) {
      return `${startDate} • ${startTime} - ${endTime}`;
    }
    return `${startDate} ${startTime} - ${endDate} ${endTime}`;
  };

 

  if (revenueError) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="text-destructive font-medium">Error loading revenue data</div>
          <div className="text-sm text-muted-foreground mt-1">{revenueError.message}</div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SummaryHeader title="Events" />
      <Card className="h-full flex flex-col">
        <CardHeader className="pb-6 flex flex-row justify-between items-center space-y-0">
          <div className="flex items-center space-x-3">
            <Calendar className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-xl font-semibold text-card-foreground">
                {selectedDate ? selectedDate.toLocaleDateString('en-US', { 
                  weekday: 'long',
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                }) : "Select a date"}
              </h1>
              {dayEvents.length > 0 && (
                <div className="text-base text-muted-foreground mt-1">
                  {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''} scheduled
                </div>
              )}
            </div>
          </div>
          <Button
            disabled={!selectedDate}
            onClick={() => setShowAdd(true)}
            className="flex items-center space-x-2 text-base px-4 py-2"
          >
            <Plus className="h-5 w-5" />
            <span>Add Event</span>
          </Button>
        </CardHeader>
        {revenueLoading ? (
          <EventsCardSkeleton />
        ) : (
        <CardContent className="flex-1">
          {dayEvents.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-8">
              <Calendar className="h-16 w-16 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-card-foreground mb-2">No events scheduled</h3>
              <p className="text-base text-muted-foreground mb-6">
                {selectedDate ? "No events for this day." : "Select a date to view events."}
              </p>
              {selectedDate && (
                <Button onClick={() => setShowAdd(true)} variant="outline" className="text-base px-6 py-2">
                  <Plus className="h-5 w-5 mr-2" />
                  Create your first event
                </Button>
              )}
            </div>
          ) : (
            <div className="max-h-[600px] overflow-y-auto">
              <div className="space-y-3 p-4">
                {dayEvents.map((evt) => {
                  console.log("evt item:", evt);
                  const metrics = calculateEventMetrics(evt, transformedData);

                  return (
                    <Card key={evt.id} className="transition-all duration-200 border-l-4" 
                          style={{ borderLeftColor: getSourceColor(evt.source) }}>
                      <CardContent className="p-5">
                        <div className="flex justify-between items-start mb-3">
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-lg text-card-foreground mb-2 truncate">
                              <div className="flex items-center gap-2">
                                <span className="truncate">{evt.title}</span>
                                {evt.channel === "retail" && (
                                  <span className="text-sm text-muted-foreground whitespace-nowrap">
                                    • {evt.location || "ALAIA BAY"}
                                  </span>
                                )}
                              </div>
                            </h3>
                            {evt.description && (
                              <p className="text-base text-muted-foreground mb-3 line-clamp-2">
                                {evt.description}
                              </p>
                            )}
                            <div className="flex items-center text-sm text-muted-foreground space-x-4">
                              <div className="flex items-center space-x-2">
                                <div 
                                  className="w-3 h-3 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: getSourceColor(evt.source) }}
                                />
                                <span className="font-medium capitalize">{evt.source}</span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Clock className="h-4 w-4 flex-shrink-0" />
                                <span className="truncate">{formatDateRange(evt.startAt, evt.endAt)}</span>
                              </div>
                            </div>
                          </div>
                          <div className="ml-4 flex-shrink-0">
                            <EventActions
                              onEdit={() => setEditEvent(evt)}
                              onDelete={() => handleDeleteEvent(evt.id)}
                              isLoading={currentDeletingId === evt.id}
                            />
                          </div>
                        </div>

                        <div className="grid grid-cols-3 gap-3 mb-3">
                          <div className="bg-muted/30 rounded-lg p-3">
                            <div className="text-sm text-muted-foreground mb-1">Before</div>
                            <div className="text-base font-semibold">
                              {metrics.beforeRevenue !== null
                                ? `${currency}${metrics.beforeRevenue.toFixed(0)}`
                                : "N/A"}
                            </div>
                          </div>
                          
                          <div className="bg-primary/5 rounded-lg p-3">
                            <div className="text-sm text-muted-foreground mb-1">During</div>
                            <div className="text-base font-semibold">
                              {metrics.duringRevenue !== null
                                ? `${currency}${metrics.duringRevenue.toFixed(0)}`
                                : "N/A"}
                            </div>
                          </div>
                          
                          <div className="bg-muted/30 rounded-lg p-3">
                            <div className="text-sm text-muted-foreground mb-1">After</div>
                            <div className="text-base font-semibold">
                              {metrics.afterRevenue !== null
                                ? `${currency}${metrics.afterRevenue.toFixed(0)}`
                                : "N/A"}
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg text-sm">
                            <div className="flex items-center space-x-2">
                              {metrics.growthBeforeToDuring !== null && metrics.growthBeforeToDuring > 0 ? (
                                <TrendingUp className="h-4 w-4 text-green-600" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-red-600" />
                              )}
                              <span className="font-medium">Before → During</span>
                            </div>
                            <span 
                              className="font-semibold text-base"
                              style={{
                                color: metrics.growthBeforeToDuring !== null && metrics.growthBeforeToDuring > 0
                                  ? "rgb(22, 163, 74)"
                                  : "rgb(220, 38, 38)"
                              }}
                            >
                              {metrics.growthBeforeToDuring !== null
                                ? `${metrics.growthBeforeToDuring > 0 ? '+' : ''}${metrics.growthBeforeToDuring.toFixed(1)}%`
                                : "N/A"}
                            </span>
                          </div>

                          <div className="flex items-center justify-between p-3 bg-background/50 rounded-lg text-sm">
                            <div className="flex items-center space-x-2">
                              {metrics.growthDuringToAfter !== null && metrics.growthDuringToAfter > 0 ? (
                                <TrendingUp className="h-4 w-4 text-green-600" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-red-600" />
                              )}
                              <span className="font-medium">During → After</span>
                            </div>
                            <span 
                              className="font-semibold text-base"
                              style={{
                                color: metrics.growthDuringToAfter !== null && metrics.growthDuringToAfter > 0
                                  ? "rgb(22, 163, 74)"
                                  : "rgb(220, 38, 38)"
                              }}
                            >
                              {metrics.growthDuringToAfter !== null
                                ? `${metrics.growthDuringToAfter > 0 ? '+' : ''}${metrics.growthDuringToAfter.toFixed(1)}%`
                                : "N/A"}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}
        </CardContent>
        )}
      </Card>

      {/* Enhanced Modal for Add Event */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
    
            <div className="p-6">
              <EventForm
                selectedDate={selectedDate}
                onSubmit={handleAddEvent}
                onCancel={() => setShowAdd(false)}
              />
            </div>

        </div>
      )}

      {/* Enhanced Modal for Edit Event */}
      {editEvent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          
            <div className="p-6">
              <EventForm
                initial={editEvent}
                selectedDate={selectedDate}
                onSubmit={(updatedEvent) => {
                  onEventsChange(
                    events.map((e) => (e.id === updatedEvent.id ? updatedEvent : e))
                  );
                  setEditEvent(null);
                }}
                onCancel={() => setEditEvent(null)}
              />
            </div>
          
        </div>
      )}
    </>
  );
};